<template>
  <div id="systems">
    <div class="atb-flex atb-flex-row atb-gap-4 atb-pt-6 atb-px-4 atb-pb-4">
      <div
        id="searchBarSection"
        class="atb-flex-shrink"
      >
        <SearchPanel
          page="systems"
          @search-clicked="search"
          @reset-clicked="reset"
        />
      </div>
      <div class="atb-flex-grow">
        <div class="eds-table-wrap atb-w-full atb-h-full">
          <Toolbar
            class-name="atb-bg-white atb-border-t-0 atb-border-x-0 atb-border-b atb-border-solid atb-border-gray-300"
          >
            <template #toolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    motif="tertiary"
                    :title="$t('refresh')"
                    aria-label="refresh button"
                    @click="refresh"
                  >
                    <template #icon>
                      <span class="material-icons">loop</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="systemListCopySystemButton"
                    motif="tertiary"
                    :title="$t('copy')"
                    aria-label="copy button"
                    @click="openCopyForm"
                  >
                    <template #icon>
                      <span class="material-icons-outlined">file_copy</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="createSystemButton"
                    motif="tertiary"
                    :title="$t('create')"
                    aria-label="create button"
                    @click="emit('create')"
                  >
                    <template #icon>
                      <span class="material-icons">add_circle_outline</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>

              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="deploySystemButton"
                    motif="tertiary"
                    :title="$t('deploy')"
                    aria-label="deploy button"
                    :disabled="readOnly"
                    @click="emit('deploy')"
                  >
                    <template #icon>
                      <span class="material-icons">file_upload</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="systemsListDeleteButton"
                    motif="tertiary"
                    :title="$t('delete')"
                    aria-label="delete button"
                    @click="openDeleteModal"
                  >
                    <template #icon>
                      <span
                        class="material-icons icon text-center"
                        style="margin-left: 25px"
                      >
                        delete_outline_icon
                      </span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
            </template>
          </Toolbar>
          <AgGridVue
            id="systemsListAllSystemsGrid"
            class="ag-theme-eds atb-w-full atb-h-full"
            :columnDefs="columnDefs"
            :components="gridComponents"
            :rowModelType="'infinite'"
            :paginationPageSize="rowsPerPage"
            :pagination="true"
            :suppressPaginationPanel="true"
            :suppressCellSelection="true"
            @grid-ready="onGridReady"
            :context="context"
          />
          <Pagination
            v-model="currentPage"
            :page-count="totalPages"
            :total-rows="totalRows"
            :page-size="rowsPerPage"
            @pageClick="changePageAndRefresh"
            @pageSizeChange="changePageSizeAndRefresh"
            class="atb-bg-white"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, Ref, toRefs, computed } from 'vue';
  import {
    ColumnApi,
    GridApi,
    GridReadyEvent,
    IGetRowsParams,
  } from 'ag-grid-community';
  import { useSystemsStore } from '@/app/features/ams/systems/systems-store';
  import { usePagination } from '@/app/shared/hooks/use-pagination';
  import { storeToRefs } from 'pinia';
  import { formatQueryParams } from '@/app/shared/utils/query-params-formatter.util';
  import { getTransformedDate } from '@/app/shared/utils/date.util';
  import { SearchPanelParams } from '@/app/shared/types/params/search-panel-params.interface';
  import SearchPanel from '@/app/features/ams/common/SearchPanel.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Toolbar from '@atb-ui/eds-vue/src/components/Toolbar/Toolbar.vue';
  import ToolbarSection from '@atb-ui/eds-vue/src/components/Toolbar/ToolbarSection.vue';
  import SystemNameRenderer from '@/app/shared/components/grid-cells/SystemNameRenderer.vue';
  import Pagination from '@atb-ui/eds-vue/src/components/Pagination.vue';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import {
    lastUpdatedRenderer,
    createdByRenderer,
  } from '@/app/shared/components/grid-cells/cellRenderers';
  import useCopyForm from '@/app/shared/hooks/use-copy-form';
  import { System } from '@/app/shared/types/system.interface';
  import { ListEvents } from '@/app/shared/types/list-events.interface';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { useI18n } from 'vue-i18n';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { TabHeader } from '@/app/shared/types/tab-header.interface';
  import AccessGuard from '@/app/shared/guards/access-guard';

  interface ListProps {
    clientId: number;
  }
  interface SystemsListEvents extends ListEvents<System> {}

  const emit = defineEmits<SystemsListEvents>();
  const props = defineProps<ListProps>();
  const { t } = useI18n();
  const { clientId } = toRefs(props);
  const systemsStore = useSystemsStore();
  const alertStore = useAlertStore();
  const { requestParams, systems, tabs } = storeToRefs(systemsStore);

  const gridApi = ref(null) as Ref<GridApi | null>;
  const columnApi = ref({}) as Ref<ColumnApi | null>;
  const {
    currentPage,
    rowsPerPage,
    totalRows,
    totalPages,
    changePage,
    changePageSize,
    calculatePagination,
  } = usePagination(
    systems.value.numberOfElements,
    systems.value.totalElements,
    gridApi
  );

  const { openCopyForm } = useCopyForm<ListEvents<System>, System>(
    gridApi,
    'system',
    emit
  );

  const readOnly = computed(() => !AccessGuard.canModify('DEPLOY'));

  const changePageAndRefresh = (page: number) => {
    changePage(page);
    gridApi.value?.setDatasource(serverSideDatasource());
  };

  const changePageSizeAndRefresh = (newPageSize: number | string) => {
    changePageSize(newPageSize);
    gridApi.value?.setDatasource(serverSideDatasource());
  };

  const openDeleteModal = () => {
    if (!isRowSelected()) {
      return alertStore.showNotification(
        t('filtersNotSelected'),
        t('systems'),
        Motif.ERROR
      );
    }

    if (isSystemOpen()) {
      return alertStore.showNotification(
        t('deleteError'),
        t('systems'),
        Motif.ERROR
      );
    }

    emit('delete');
  };

  const columnDefs = [
    {
      field: 'id',
      headerName: '',
      suppressMovable: true,
      width: 50,
      minWidth: 50,
      sortable: false,
      checkboxSelection: true,
      valueFormatter: () => '',
    },
    {
      field: 'systemName',
      headerName: 'System Name',
      width: 150,
      sortable: true,
      cellRendererFramework: SystemNameRenderer,
    },
    {
      field: 'updatedAt',
      headerName: 'Last Updated',
      sortable: true,
      cellRenderer: 'lastUpdatedRenderer',
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      sortable: true,
      cellRenderer: 'createdByRenderer',
    },
  ];

  const gridComponents = {
    lastUpdatedRenderer,
    createdByRenderer,
  };

  const isRowSelected = () => {
    return (
      gridApi.value?.getSelectedRows().map((filter) => filter.id).length !== 0
    );
  };

  const isSystemOpen = () => {
    const selectedSystem: Array<number> | undefined = gridApi.value
      ?.getSelectedRows()
      .map((system: System) => system.id);
    return tabs.value
      .map((tab: TabHeader) => {
        return selectedSystem?.find((id) => id === tab.id);
      })
      .find((id?: number) => typeof id !== 'undefined');
  };

  const onGridReady = (params: GridReadyEvent) => {
    gridApi.value = params.api;
    columnApi.value = params.columnApi;
    gridApi.value.setDatasource(serverSideDatasource());
    gridApi.value.sizeColumnsToFit();
  };

  const systemNameClick = async (systemId: number, systemName: string) => {
    emit('createNewTab', {
      clientId: clientId.value,
      id: systemId,
      name: systemName,
      isNew: true,
      dirtyTab: new Map<string, boolean>(),
    });
  };

  const search = (searchPanelParams: SearchPanelParams) => {
    systemsStore.updateRequestParams({
      systemName: searchPanelParams.nameSearchField
        ? searchPanelParams.nameSearchField
        : undefined,
      createdBy: searchPanelParams.createdUserSelected
        ? searchPanelParams.createdUserSelected
        : undefined,
      fromCreatedAt: searchPanelParams.createdFromDate
        ? getTransformedDate(searchPanelParams.createdFromDate)
        : undefined,
      toCreatedAt: searchPanelParams.createdToDate
        ? getTransformedDate(searchPanelParams.createdToDate)
        : undefined,
      fromUpdatedAt: searchPanelParams.updatedFromDate
        ? getTransformedDate(searchPanelParams.updatedFromDate)
        : undefined,
      toUpdatedAt: searchPanelParams.updatedToDate
        ? getTransformedDate(searchPanelParams.updatedToDate)
        : undefined,
    });
    changePageAndRefresh(1);
  };

  const serverSideDatasource = () => {
    return {
      getRows: async ({
        sortModel,
        successCallback,
        failCallback,
      }: IGetRowsParams) => {
        try {
          gridApi.value?.showLoadingOverlay();
          await systemsStore.fetchAllSystemsByClient(clientId.value);
          await systemsStore.fetchSystemMetaData(
            clientId.value,
            formatQueryParams(sortModel, {
              ...requestParams.value,
              page: currentPage.value > 0 ? currentPage.value - 1 : 0,
              size: rowsPerPage.value,
            })
          );
          if (systems.value.numberOfElements > 0) {
            gridApi.value?.hideOverlay();
          } else {
            gridApi.value?.showNoRowsOverlay();
          }
          calculatePagination(systems.value.totalElements);
          successCallback(
            systems.value.content || [],
            systems.value.numberOfElements || 0
          );
        } catch (error) {
          gridApi.value?.showNoRowsOverlay();
          failCallback();
          console.error((error as Error).message);
        }
      },
    };
  };

  const reset = (): void => {
    requestParams.value = {};
    systemsStore.updateRequestParams({});
    gridApi.value?.setDatasource(serverSideDatasource());
  };

  const refresh = (): void => {
    gridApi.value?.purgeInfiniteCache();
  };

  const context = {
    systemNameClickCallback: systemNameClick,
  };

  defineExpose({
    refresh,
    gridApi,
  });
</script>
<style scoped>
  .toolbar-section * {
    width: 50px;
  }
</style>
